import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  Calendar, Clock, Video, User, Mail, FileText, Link as LinkIcon,
  Edit, DollarSign
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const AdminConsultations = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [consultations, setConsultations] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  const [editForm, setEditForm] = useState({
    meeting_link: "",
    status: "",
    counselor_name: "",
    notes: ""
  });

  useEffect(() => {
    if (user?.role !== "admin" && user?.role !== "school_admin") {
      toast.error("Access denied: Admin only");
      navigate("/");
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [consultRes, transRes] = await Promise.all([
        axios.get(`${API}/admin/consultations/all`, { 
          headers: { Authorization: `Bearer ${token}` } 
        }),
        axios.get(`${API}/admin/transactions`, { 
          headers: { Authorization: `Bearer ${token}` } 
        })
      ]);
      
      setConsultations(consultRes.data.consultations);
      setTransactions(transRes.data.transactions);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (consultation) => {
    setSelectedConsultation(consultation);
    setEditForm({
      meeting_link: consultation.meeting_link || "",
      status: consultation.status,
      counselor_name: consultation.counselor_name || "",
      notes: consultation.notes || ""
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    try {
      await axios.patch(
        `${API}/admin/consultations/${selectedConsultation.id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Consultation updated");
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to update consultation");
    }
  };

  const createTestConsultation = async () => {
    try {
      await axios.post(
        `${API}/admin/consultations/create-test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Test consultation created");
      fetchData();
    } catch (error) {
      toast.error("Failed to create test consultation");
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case "confirmed": return "bg-green-500";
      case "pending": return "bg-yellow-500";
      case "completed": return "bg-blue-500";
      case "cancelled": return "bg-red-500";
      default: return "bg-gray-500";
    }
  };

  const getPaymentStatusColor = (status) => {
    switch(status) {
      case "paid": return "bg-green-500";
      case "pending": return "bg-yellow-500";
      case "failed": return "bg-red-500";
      default: return "bg-gray-500";
    }
  };

  const totalRevenue = consultations
    .filter(c => c.payment_status === "paid" || c.status === "completed")
    .reduce((sum, c) => sum + (c.amount || 0), 0);

  // Get user's currency settings
  const [currencySymbol, setCurrencySymbol] = React.useState("₹");
  
  React.useEffect(() => {
    const fetchCurrencySettings = async () => {
      try {
        const response = await axios.get(`${API}/currency-settings`);
        setCurrencySymbol(response.data.symbol || "₹");
      } catch (error) {
        console.error("Failed to fetch currency settings");
      }
    };
    fetchCurrencySettings();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Consultation Management</h1>
          <p className="text-gray-600">Manage bookings and add meeting links</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{consultations.length}</p>
                <p className="text-sm text-gray-600">Total Bookings</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">
                  {consultations.filter(c => c.status === "confirmed").length}
                </p>
                <p className="text-sm text-gray-600">Confirmed</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600">
                  {consultations.filter(c => c.status === "pending").length}
                </p>
                <p className="text-sm text-gray-600">Pending</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">{currencySymbol}{totalRevenue.toFixed(2)}</p>
                <p className="text-sm text-gray-600">Total Revenue</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Consultations List */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold mb-4">All Consultations</h2>
          
          {loading ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-gray-600">Loading consultations...</p>
              </CardContent>
            </Card>
          ) : consultations.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Video className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-semibold mb-2">No consultations booked yet</h3>
                <p className="text-gray-600 mb-4">
                  Students can book consultations from the "Book Consultation" page. 
                  Once booked and paid, they will appear here for you to manage.
                </p>
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" onClick={createTestConsultation}>
                    Create Test Consultation
                  </Button>
                  <Button onClick={() => navigate("/consultation/book")}>
                    Go to Booking Page
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            consultations.map((consultation) => (
              <Card key={consultation.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{consultation.topic}</CardTitle>
                      <div className="flex gap-2 mt-2">
                        <Badge className={getStatusColor(consultation.status)}>
                          {consultation.status.toUpperCase()}
                        </Badge>
                        <Badge className={getPaymentStatusColor(consultation.payment_status)}>
                          {consultation.payment_status.toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-green-600">${consultation.amount}</p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(consultation)}
                        className="mt-2"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="font-medium">{consultation.user_name}</p>
                        <p className="text-xs text-gray-600">{consultation.user_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span>{new Date(consultation.scheduled_date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span>{consultation.scheduled_time}</span>
                    </div>
                    {consultation.counselor_name && (
                      <div className="flex items-center gap-2 text-sm">
                        <User className="w-4 h-4 text-gray-500" />
                        <span>Counselor: {consultation.counselor_name}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <span>ID: {consultation.id.substring(0, 8)}</span>
                    </div>
                  </div>

                  {consultation.notes && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm">
                        <strong>User Notes:</strong> {consultation.notes}
                      </p>
                    </div>
                  )}

                  {consultation.meeting_link && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <LinkIcon className="w-4 h-4 text-green-600" />
                          <span className="text-sm font-medium text-green-800">Meeting Link Set</span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(consultation.meeting_link, '_blank')}
                        >
                          View Link
                        </Button>
                      </div>
                    </div>
                  )}

                  {!consultation.meeting_link && consultation.payment_status === "paid" && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        ⚠️ Meeting link not added yet. Click Edit to add Zoom/Meet/Teams link.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Edit Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Update Consultation</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Meeting Link (Zoom/Meet/Teams) *</Label>
                <Input
                  value={editForm.meeting_link}
                  onChange={(e) => setEditForm({...editForm, meeting_link: e.target.value})}
                  placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  User will receive this link via email and dashboard
                </p>
              </div>

              <div>
                <Label>Status</Label>
                <Select 
                  value={editForm.status}
                  onValueChange={(val) => setEditForm({...editForm, status: val})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Assign Counselor</Label>
                <Input
                  value={editForm.counselor_name}
                  onChange={(e) => setEditForm({...editForm, counselor_name: e.target.value})}
                  placeholder="Counselor name"
                />
              </div>

              <div>
                <Label>Admin Notes</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  placeholder="Any notes about this consultation..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button onClick={handleUpdate}>
                Update Consultation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Recent Transactions */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4">Recent Transactions</h2>
          
          {/* Mobile Card View */}
          <div className="block md:hidden space-y-3">
            {transactions.slice(0, 10).map((transaction) => (
              <Card key={transaction.id} className="p-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-sm">{transaction.transaction_type}</p>
                      <p className="text-xs text-gray-600">{transaction.user_email}</p>
                    </div>
                    <Badge className={getPaymentStatusColor(transaction.payment_status)}>
                      {transaction.payment_status}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t">
                    <span className="text-sm font-medium">${transaction.amount}</span>
                    <span className="text-xs text-gray-500">
                      {new Date(transaction.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Desktop Table View */}
          <Card className="hidden md:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {transactions.slice(0, 10).map((transaction) => (
                      <tr key={transaction.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{transaction.transaction_type}</td>
                        <td className="px-4 py-3 text-sm">{transaction.user_email}</td>
                        <td className="px-4 py-3 text-sm font-medium">${transaction.amount}</td>
                        <td className="px-4 py-3">
                          <Badge className={getPaymentStatusColor(transaction.payment_status)}>
                            {transaction.payment_status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {new Date(transaction.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AdminConsultations;
